import { useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SCHOOLS, validateRegister } from '@crossmatch/shared';
import type { SchoolId } from '@crossmatch/shared';
import { authApi } from '../api/auth.js';
import type { RegisterInput } from '../api/auth.js';
import { toUserMessage } from '../api/errors.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { VerifyEmail } from '../components/VerifyEmail.js';
import { safeRedirectTarget } from '../lib/navigation.js';
import { useSession } from '../store/session.js';
import { toast } from '../store/toast.js';

type FieldName = 'email' | 'password' | 'name' | 'school' | 'acceptedTerms';

/** 注册页：校验规则直接复用 shared 的 validateRegister（与后端同一套，不会前后端打架）。 */
export function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordAgain, setPasswordAgain] = useState('');
  const [name, setName] = useState('');
  const [school, setSchool] = useState<SchoolId | ''>('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<FieldName | 'passwordAgain', string>>
  >({});
  const [formError, setFormError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'form' | 'verify'>('form');

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const setSession = useSession((state) => state.setSession);

  const register = useMutation({
    mutationFn: (input: RegisterInput) => authApi.register(input),
    onSuccess: (data) => {
      // 注册即登录（后端返回会话），但邮箱尚未验证：进入内嵌验证视图，验证完再进首页。
      setPhase('verify');
      setSession({ user: data.user, accessToken: data.accessToken, refreshToken: data.refreshToken });
      queryClient.clear();
    },
    onError: (error) => {
      setFormError(toUserMessage(error));
    },
  });

  // 已登录（且是进页面时就已经登录）的人不该再看注册表单。
  // 用挂载时的快照而不是实时 authed 判断：注册成功会同步写 session 触发重渲染，
  // 若用实时值会在 phase 尚未切到 verify 的空档把人直接送进 /profile，跳过邮箱验证。
  const authedAtMount = useRef<boolean>(
    Boolean(useSession.getState().accessToken) || Boolean(useSession.getState().refreshToken),
  );
  if (authedAtMount.current && phase === 'form') return <Navigate to="/profile" replace />;

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const parsed = validateRegister({ email, password, name, school });
    const next: Partial<Record<FieldName | 'passwordAgain', string>> = {};
    if (!parsed.ok) {
      for (const issue of parsed.issues) {
        if (
          issue.field === 'email' ||
          issue.field === 'password' ||
          issue.field === 'name' ||
          issue.field === 'school'
        ) {
          next[issue.field] = issue.message;
        }
      }
    }
    if (password !== passwordAgain) next.passwordAgain = '两次输入的密码不一致';
    if (!acceptedTerms) next.acceptedTerms = '请先阅读并勾选同意《用户协议》和《隐私政策》';
    if (!parsed.ok || !parsed.value || Object.keys(next).length > 0) {
      setFieldErrors(next);
      setFormError('注册信息有误：请按下方标红提示修正后再提交');
      return;
    }
    setFieldErrors({});
    setFormError(null);
    register.mutate({ ...parsed.value, consent: acceptedTerms });
  };

  return (
    <div className="auth-shell">
      <Card
        className="auth-card"
        title={phase === 'verify' ? '验证邮箱以激活账号' : '注册 CrossMatch'}
        subtitle={
          phase === 'verify'
            ? '注册成功！为安全起见，请验证邮箱后再继续使用。'
            : '只需要邮箱与昵称；不收集学号、手机号、身份证。学校用于跨校匹配判定。'
        }
      >
        {phase === 'verify' ? (
          <VerifyEmail
            email={email}
            onVerified={() => navigate(safeRedirectTarget(location.state, '/home'), { replace: true })}
            hint={`我们已向 ${email} 发送了验证码，请查收邮件并输入以激活账号；若没收到可点「重新发送」。`}
          />
        ) : (
        <form className="stack" onSubmit={handleSubmit} noValidate>
          {formError !== null ? (
            <div className="banner banner-danger" role="alert">
              <span className="banner-ico" aria-hidden="true">
                !
              </span>
              <span className="wrap-anywhere">{formError}</span>
            </div>
          ) : null}

          <div className="field">
            <label className="field-label" htmlFor="register-email">
              邮箱
            </label>
            <input
              id="register-email"
              className={fieldErrors.email !== undefined ? 'input input-invalid' : 'input'}
              type="email"
              value={email}
              autoComplete="email"
              placeholder="you@example.com"
              onChange={(event) => setEmail(event.target.value)}
            />
            {fieldErrors.email !== undefined ? <span className="field-error">{fieldErrors.email}</span> : null}
          </div>

          <div className="field">
            <label className="field-label" htmlFor="register-name">
              姓名 / 昵称（上限 24 字）
            </label>
            <input
              id="register-name"
              className={fieldErrors.name !== undefined ? 'input input-invalid' : 'input'}
              value={name}
              maxLength={24}
              autoComplete="nickname"
              placeholder="如：小何"
              onChange={(event) => setName(event.target.value)}
            />
            {fieldErrors.name !== undefined ? <span className="field-error">{fieldErrors.name}</span> : null}
          </div>

          <div className="field">
            <label className="field-label" htmlFor="register-school">
              学校
            </label>
            <select
              id="register-school"
              className={fieldErrors.school !== undefined ? 'select input-invalid' : 'select'}
              value={school}
              onChange={(event) => setSchool(event.target.value as SchoolId | '')}
            >
              <option value="">请选择学校</option>
              {SCHOOLS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            {fieldErrors.school !== undefined ? <span className="field-error">{fieldErrors.school}</span> : null}
            <span className="field-hint">松江七校 + 其他高校；跨校匹配会用到这个字段。</span>
          </div>

          <div className="field">
            <label className="field-label" htmlFor="register-password">
              密码（8–72 位）
            </label>
            <input
              id="register-password"
              className={fieldErrors.password !== undefined ? 'input input-invalid' : 'input'}
              type="password"
              value={password}
              autoComplete="new-password"
              placeholder="至少 8 位，建议含大小写与数字"
              onChange={(event) => setPassword(event.target.value)}
            />
            {fieldErrors.password !== undefined ? <span className="field-error">{fieldErrors.password}</span> : null}
          </div>

          <div className="field">
            <label className="field-label" htmlFor="register-password-again">
              再输一次密码
            </label>
            <input
              id="register-password-again"
              className={fieldErrors.passwordAgain !== undefined ? 'input input-invalid' : 'input'}
              type="password"
              value={passwordAgain}
              autoComplete="new-password"
              onChange={(event) => setPasswordAgain(event.target.value)}
            />
            {fieldErrors.passwordAgain !== undefined ? (
              <span className="field-error">{fieldErrors.passwordAgain}</span>
            ) : null}
          </div>

          <div className="field">
            <label className="checkbox" htmlFor="register-consent">
              <input
                id="register-consent"
                type="checkbox"
                checked={acceptedTerms}
                onChange={(event) => setAcceptedTerms(event.target.checked)}
              />
              <span>
                已阅读并同意
                <Link to="/terms" target="_blank" rel="noreferrer">
                  {' '}
                  《用户协议》
                </Link>
                与
                <Link to="/privacy" target="_blank" rel="noreferrer">
                  {' '}
                  《隐私政策》
                </Link>
              </span>
            </label>
            {fieldErrors.acceptedTerms !== undefined ? (
              <span className="field-error">{fieldErrors.acceptedTerms}</span>
            ) : null}
          </div>

          <Button type="submit" variant="primary" className="btn-block" loading={register.isPending}>
            {register.isPending ? '正在注册…' : '注册并填写画像'}
          </Button>

          <p className="auth-foot">
            已有账号？<Link to="/login">去登录</Link>
          </p>
        </form>
        )}
      </Card>
    </div>
  );
}